import { LegalLayout } from "@/components/site/LegalLayout";

const LAST_UPDATED = "July 14, 2026";

const intro = [
  {
    type: "p",
    text: "These Terms of Use (\"Terms\") govern your access to and use of the website, programs, certifications, research, resources, membership offerings, and other services (collectively, the \"Services\") provided by the World Financial Innovation Institute (\"WFII\", \"we\", \"us\", or \"our\"). By accessing or using the Services, you agree to be bound by these Terms. If you do not agree with these Terms, please do not use the Services.",
  },
  {
    type: "p",
    text: "If you are using the Services on behalf of an organization, you represent that you have the authority to bind that organization to these Terms, and \"you\" refers to both you and that organization.",
  },
];

const sections = [
  {
    id: "about-wfii",
    title: "About WFII",
    body: [
      {
        type: "p",
        text: "WFII is a Canada-based educational institute providing professional education, certification, research, and resources on digital finance, including digital money, stablecoins, digital assets, payments, accounting, treasury, and compliance. Our audience includes CFOs, accountants, bookkeepers, treasury teams, compliance professionals, business leaders, educators, and institutions.",
      },
    ],
  },
  {
    id: "educational-purpose",
    title: "Educational Purpose — No Professional Advice",
    body: [
      {
        type: "p",
        text: "All content made available through the Services — including programs, certifications, guides, reports, checklists, glossaries, frameworks, and other materials — is provided for general educational and informational purposes only.",
      },
      {
        type: "ul",
        items: [
          "Nothing in the Services constitutes financial, investment, legal, tax, accounting, audit, or other professional advice.",
          "Nothing in the Services is an offer, solicitation, or recommendation to buy, sell, or hold any digital asset, stablecoin, security, or other financial instrument.",
          "Digital assets involve significant risks, and regulatory treatment varies by jurisdiction and changes over time. You are responsible for evaluating the applicability of any information to your circumstances.",
          "You should consult qualified professional advisors before making decisions that may have financial, legal, tax, or regulatory consequences for you or your organization.",
        ],
      },
      {
        type: "p",
        text: "Completion of a WFII program or certification demonstrates participation in an educational curriculum and, where applicable, achievement on related assessments. It does not constitute a professional license, regulatory approval, or a guarantee of competence in any specific engagement.",
      },
    ],
  },
  {
    id: "eligibility-accounts",
    title: "Eligibility and Accounts",
    body: [
      {
        type: "p",
        text: "The Services are intended for individuals who have reached the age of majority in their province, territory, or country of residence. Where the Services allow you to create an account or registration profile, you agree to provide accurate and complete information and to keep it up to date. You are responsible for maintaining the confidentiality of your credentials and for all activity under your account. Notify us promptly at info@wfii.ca if you suspect unauthorized use of your account.",
      },
    ],
  },
  {
    id: "programs-certifications",
    title: "Programs, Certifications, and Enrollment",
    body: [
      {
        type: "ul",
        items: [
          "Enrollment in programs and certification pathways is subject to availability, any published prerequisites, and payment of applicable fees.",
          "We may update program content, schedules, instructors, delivery formats, and assessment requirements to keep materials current with an evolving industry.",
          "Certifications may be subject to renewal, continuing education, or code-of-conduct requirements as published by WFII from time to time.",
          "We may revoke or suspend a certification in cases of academic dishonesty, misrepresentation, or misuse of WFII credentials or trademarks.",
          "Program materials are licensed to enrolled participants for personal professional development and may not be redistributed, resold, or used to deliver competing training without our prior written consent.",
        ],
      },
    ],
  },
  {
    id: "payments-refunds",
    title: "Payments and Refunds",
    body: [
      {
        type: "p",
        text: "Fees for programs, certifications, memberships, and partnerships are as published at the time of purchase or as set out in a written agreement. Unless otherwise stated, fees are quoted in Canadian dollars and exclude applicable taxes. Payments are processed by third-party payment processors. Refund and cancellation terms are stated at the point of registration for each program or membership; where no specific terms are stated, refund requests will be considered on a reasonable, case-by-case basis. We may change our fees prospectively with notice.",
      },
    ],
  },
  {
    id: "intellectual-property",
    title: "Intellectual Property",
    body: [
      {
        type: "p",
        text: "The Services and all content made available through them — including text, curricula, assessments, guides, reports, checklists, glossaries, frameworks, graphics, logos, and the WFII name and marks — are owned by WFII or its licensors and are protected by copyright, trademark, and other intellectual property laws.",
      },
      {
        type: "ul",
        items: [
          "You may view, download, and print content from the website for your personal, non-commercial professional use, provided you keep all copyright and proprietary notices intact.",
          "You may not reproduce, distribute, modify, create derivative works from, publicly display, or commercially exploit any content without our prior written permission.",
          "You may not use the WFII name, logo, or certification marks except as expressly permitted in writing or under published credential usage guidelines.",
        ],
      },
    ],
  },
  {
    id: "acceptable-use",
    title: "Acceptable Use",
    body: [
      { type: "p", text: "You agree not to:" },
      {
        type: "ul",
        items: [
          "Use the Services in violation of any applicable law or regulation.",
          "Interfere with or disrupt the operation or security of the Services, or attempt to gain unauthorized access to any systems, accounts, or data.",
          "Use automated means to scrape, harvest, or index the Services without our prior written consent.",
          "Share assessment content, answers, or other examination materials, or engage in any form of academic dishonesty.",
          "Misrepresent your identity, affiliation, credentials, or certification status.",
          "Upload or transmit malicious code or content that is unlawful, defamatory, or infringing.",
        ],
      },
    ],
  },
  {
    id: "user-content",
    title: "User Content",
    body: [
      {
        type: "p",
        text: "Where the Services allow you to submit content — such as questions, discussion posts, survey responses, or feedback — you retain ownership of your content but grant WFII a non-exclusive, worldwide, royalty-free licence to use, reproduce, and display that content for the purposes of operating and improving the Services. You are responsible for ensuring that your content does not infringe the rights of others and does not contain confidential information you are not permitted to share.",
      },
    ],
  },
  {
    id: "third-party-services",
    title: "Third-Party Services and Links",
    body: [
      {
        type: "p",
        text: "The Services may reference or link to third-party websites, tools, publications, or services. These references are provided for convenience and educational context only and do not constitute an endorsement. WFII is not responsible for the content, accuracy, or practices of third parties, and your use of third-party services is at your own risk and subject to their terms.",
      },
    ],
  },
  {
    id: "disclaimers",
    title: "Disclaimers",
    body: [
      {
        type: "p",
        text: "The Services are provided on an \"as is\" and \"as available\" basis. To the maximum extent permitted by applicable law, WFII disclaims all warranties, conditions, and representations of any kind, whether express, implied, or statutory, including implied warranties of merchantability, fitness for a particular purpose, non-infringement, and any warranties arising from course of dealing or usage of trade.",
      },
      {
        type: "p",
        text: "While we work to keep content accurate and current, the digital finance landscape evolves quickly, and we do not warrant that any content is complete, accurate, up to date, or suitable for your specific circumstances, or that the Services will be uninterrupted or error-free.",
      },
    ],
  },
  {
    id: "limitation-of-liability",
    title: "Limitation of Liability",
    body: [
      {
        type: "p",
        text: "To the maximum extent permitted by applicable law, WFII and its directors, officers, employees, advisors, contributors, and partners will not be liable for any indirect, incidental, special, consequential, exemplary, or punitive damages, or for any loss of profits, revenue, data, goodwill, or business opportunity, arising out of or related to your use of, or inability to use, the Services, even if we have been advised of the possibility of such damages.",
      },
      {
        type: "p",
        text: "To the maximum extent permitted by applicable law, our total aggregate liability for all claims arising out of or relating to the Services or these Terms will not exceed the greater of (a) the amount you paid to WFII for the Services giving rise to the claim in the twelve months preceding the event, and (b) one hundred Canadian dollars (CAD $100). Some jurisdictions do not allow certain limitations, so some of the above may not apply to you.",
      },
    ],
  },
  {
    id: "indemnification",
    title: "Indemnification",
    body: [
      {
        type: "p",
        text: "You agree to indemnify and hold harmless WFII and its directors, officers, employees, advisors, contributors, and partners from and against any claims, liabilities, damages, losses, and expenses, including reasonable legal fees, arising out of or related to your breach of these Terms, your misuse of the Services, or your violation of any law or the rights of a third party.",
      },
    ],
  },
  {
    id: "suspension-termination",
    title: "Suspension and Termination",
    body: [
      {
        type: "p",
        text: "We may suspend or terminate your access to all or part of the Services, with or without notice, if we reasonably believe you have violated these Terms, if required by law, or if continued provision of the Services is no longer commercially reasonable. Upon termination, provisions that by their nature should survive — including intellectual property, disclaimers, limitations of liability, and indemnification — will continue to apply.",
      },
    ],
  },
  {
    id: "governing-law",
    title: "Governing Law and Disputes",
    body: [
      {
        type: "p",
        text: "These Terms are governed by and construed in accordance with the laws of the Province of Ontario and the federal laws of Canada applicable therein, without regard to conflict of laws principles. You agree that the courts of the Province of Ontario will have exclusive jurisdiction over any dispute arising out of or relating to these Terms or the Services, and you consent to the personal jurisdiction of those courts. Nothing in this section limits any mandatory consumer protection rights available to you under the laws of your place of residence.",
      },
    ],
  },
  {
    id: "changes",
    title: "Changes to These Terms",
    body: [
      {
        type: "p",
        text: "We may update these Terms from time to time. When we make material changes, we will update the \"Last updated\" date at the top of this page and, where appropriate, provide additional notice through the Services. Your continued use of the Services after changes take effect constitutes acceptance of the updated Terms.",
      },
    ],
  },
  {
    id: "contact",
    title: "Contact Us",
    body: [
      {
        type: "p",
        text: "If you have questions about these Terms of Use, please contact us at info@wfii.ca.",
      },
    ],
  },
];

export default function TermsOfUse() {
  return (
    <LegalLayout
      title="Terms of Use"
      lastUpdated={LAST_UPDATED}
      intro={intro}
      sections={sections}
      testId="terms-of-use-page"
    />
  );
}
